import React, { type FC, useState } from "react";
import NiceModal, {
  type NiceModalHocProps,
  useModal
} from "@ebay/nice-modal-react";
import KC_Modal_2 from "../../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import { KC_Text_Sizes, KC_Weights } from "../../../../designsystem/ds_commons";
import { tt } from "../../../../utils/i18n/i18n";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Checkbox from "../../../../designsystem/KC_FormInput/KC_Checkbox";

interface Props {}

export interface UnsubscriptionReason {
  key: string;
  label: string;
  selected: boolean;
}

const UNSUBSCRIPTION_REASONS: UnsubscriptionReason[] = [
  {
    key: "objectives_reached",
    label: tt("unsubscribe_reason_objectives_reached"),
    selected: false
  },
  {
    key: "no_prizes",
    label: tt("unsubscribe_reason_no_prizes"),
    selected: false
  },
  {
    key: "avoid_renewal",
    label: tt("unsubscribe_reason_avoid_renewal"),
    selected: false
  },
  {
    key: "competitor",
    label: tt("unsubscribe_reason_competitor"),
    selected: false
  },
  {
    key: "temporary",
    label: tt("unsubscribe_reason_temporary"),
    selected: false
  },
  { key: "no_roi", label: tt("unsubscribe_reason_no_roi"), selected: false },
  {
    key: "customers_bored",
    label: tt("unsubscribe_reason_customers_bored"),
    selected: false
  },
  {
    key: "not_adapted",
    label: tt("unsubscribe_reason_not_adapted"),
    selected: false
  },
  {
    key: "no_budget",
    label: tt("unsubscribe_reason_no_budget"),
    selected: false
  },
  {
    key: "closing_business",
    label: tt("unsubscribe_reason_closing_business"),
    selected: false
  }
];

const UnsubscribeReasonsModal: FC<Props & NiceModalHocProps> =
  NiceModal.create<Props>((props) => {
    const modal = useModal();
    const [reasons, setReasons] = useState<UnsubscriptionReason[]>(
      UNSUBSCRIPTION_REASONS
    );
    const [error, setError] = useState<string>("");

    const handleReasonToggle = (key: string) => {
      setReasons((prev) =>
        prev.map((reason) =>
          reason.key === key
            ? { ...reason, selected: !reason.selected }
            : reason
        )
      );
      setError("");
    };

    const handleConfirm = () => {
      const selectedReasons = reasons.filter((r) => r.selected);
      if (selectedReasons.length === 0) {
        setError(tt("unsubscribe_reason_error"));
        return;
      }

      modal.resolve(selectedReasons);
      void modal.hide();
    };

    const handleCancel = () => {
      modal.resolve(null);
      void modal.hide();
    };

    return (
      <KC_Modal_2
        id="unsubscribe-reasons-modal"
        isOpen={modal.visible}
        onClose={handleCancel}
        hideCloseButton={true}
        extraStyle={{ width: "40%" }}
        header={
          <div className={"w-100 py-2 px-3"}>
            <KC_Heading3 text={tt("unsubscribe_reasons_title")} />
          </div>
        }
        footer={
          <div className="d-flex flex-column w-100">
            {error && (
              <div className="text-danger mb-2 px-3">
                <KC_UIText weight={KC_Weights.reg} text={error} />
              </div>
            )}
            <div className="d-flex flex-grow-1 align-items-center justify-content-center">
              <KC_Button
                id="unsubscribe-reasons-confirm"
                isPrimary={true}
                onClick={handleConfirm}
                size={KC_ButtonSize.Large}
                text={tt("unsubscribe_confirm_button")}
                className="d-flex"
              />
            </div>
          </div>
        }
      >
        <div className="p-4" style={{ overflowY: "auto" }}>
          <KC_Text
            size={KC_Text_Sizes.md}
            weight={KC_Weights.reg}
            text={tt("unsubscribe_reasons_message")}
            className="mb-3"
          />
          <div className="d-flex flex-column st-gap mt-4">
            {reasons.map((reason) => (
              <KC_Checkbox
                key={reason.key}
                id={`reason-${reason.key}`}
                label={reason.label}
                checked={reason.selected}
                onChange={() => {
                  handleReasonToggle(reason.key);
                }}
              />
            ))}
          </div>
        </div>
      </KC_Modal_2>
    );
  });

export default UnsubscribeReasonsModal;
